import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Paging,
  Select,
  Wrapper,
  useBreadcrumbRoutes,
  useSearchParams,
  Radio,
  Empty,
  Button,
  Modal,
} from '@maxtropy/components';
import { ExpandOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import cls from 'classnames';
import {
  apiV2AreaCameraListPost,
  apiV2AreaCameraPagePost,
  apiV2DistributionCabinetMonitorCode2UrlPost,
  apiV2AreaList2Post,
} from '@maxtropy/device-customer-apis-v2';
import styles from './index.module.scss';
import VideoCardPreview from './components/VideoCardPreview';
import {
  HlsVideo,
  HKPlayerFrame,
  RtspPlayerFrame,
  getEzvizTranscodeUrl,
  isEzopenUrl,
  isRtspUrl,
  shouldResolveEzvizUrl,
} from '@/components/RecorderPreview';
import { AccessMode, ConfigType } from '@/pages/VideoMonitoring/type';

interface VideoCardItem {
  renderKey: string;
  id: number | string;
  areaId: string | number;
  areaName: string;
  monitorDeviceId: string;
  monitorName: string;
  accessType?: number;
  accountAccessMode?: number;
  configType?: number;
  platformId?: number;
  thirdCode?: string;
  monitorUrl?: string;
  monitorLocation?: string;
  cameraUsername?: string;
  cameraPassword?: string;
  ipAddress?: string;
  port?: number;
  cameraPort?: number;
  rtspPort?: number;
  channelIndex?: number;
  enableFeature?: string;
}

const canPlayHK = (item?: VideoCardItem) =>
  !!item?.ipAddress && !!item?.port && !!item?.cameraUsername && !!item?.cameraPassword && !!item?.rtspPort;

const gridOptions = [
  { key: '2x2', label: '2x2', columns: 2, rows: 2, pageSize: 4 },
  { key: '3x3', label: '3x3', columns: 3, rows: 3, pageSize: 9 },
  { key: '4x4', label: '4x4', columns: 4, rows: 4, pageSize: 16 },
  { key: '4x3', label: '4x3', columns: 4, rows: 3, pageSize: 12 },
  { key: '5x3', label: '5x3', columns: 5, rows: 3, pageSize: 15 },
];

const VideoMonitorView: FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [areaOptions, setAreaOptions] = useState<Array<{ label: string; value: number }>>([]);
  const [monitorOptions, setMonitorOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [areaIds, setAreaIds] = useState<number[]>([]);
  const [monitorIds, setMonitorIds] = useState<string[]>([]);
  const [layoutKey, setLayoutKey] = useState<string>('2x2');
  const [remoteList, setRemoteList] = useState<VideoCardItem[]>([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fullScreenName, setFullScreenName] = useState('');
  const [fullScreenItem, setFullScreenItem] = useState<VideoCardItem>();
  const [fullScreenCode, setFullScreenCode] = useState<string>();
  const [fullScreenUrl, setFullScreenUrl] = useState<string>();
  const [fullScreenSize, setFullScreenSize] = useState({ width: 960, height: 540 });
  const latestPageRequestIdRef = useRef(0);

  const getMonitorUrl = async (item: VideoCardItem) => {
    const isPlatformAccess = item.accessType === 1;
    const isPlatformHK =
      isPlatformAccess && (item.accountAccessMode === AccessMode.HK || (!item.accountAccessMode && canPlayHK(item)));
    if (isPlatformHK) {
      return undefined;
    }
    if (isPlatformAccess && item.accountAccessMode === AccessMode.RTSP) {
      return item.monitorUrl;
    }
    const isPlatformYsy =
      isPlatformAccess &&
      (item.accountAccessMode === AccessMode.YSY ||
        (item.accountAccessMode == null &&
          (item.configType === ConfigType.PROXY || isEzopenUrl(item.monitorUrl) || !item.monitorUrl)));
    if (
      isPlatformYsy &&
      shouldResolveEzvizUrl({
        accessMode: AccessMode.YSY,
        configType: item.configType,
        url: item.monitorUrl,
        deviceId: item.platformId,
      })
    ) {
      return getEzvizTranscodeUrl(item.platformId);
    }
    if (isPlatformYsy && (item.configType === ConfigType.PROXY || !item.monitorUrl || isEzopenUrl(item.monitorUrl))) {
      return undefined;
    }
    if (item.monitorUrl) {
      return item.monitorUrl;
    }
    if (isPlatformYsy && item.platformId != null) {
      return getEzvizTranscodeUrl(item.platformId);
    }
    if (item.accessType === 2 && item.thirdCode) {
      const res = await apiV2DistributionCabinetMonitorCode2UrlPost({ code: item.thirdCode });
      return res?.url;
    }
    return undefined;
  };

  const openFullScreen = async (item: VideoCardItem) => {
    setFullScreenName(`${item.areaName}-${item.monitorName}`);
    setFullScreenItem(item);
    setFullScreenCode(item.accessType === 2 ? item.thirdCode : undefined);
    setFullScreenUrl(undefined);
    setIsFullScreen(true);
    const url = await getMonitorUrl(item);
    setFullScreenUrl(url);
  };

  useEffect(() => {
    if (!isFullScreen) {
      return;
    }
    const updateModalSize = () => {
      const maxWidth = window.innerWidth * 0.6;
      const maxHeight = window.innerHeight * 0.7;
      const widthByHeightLimit = (maxHeight * 16) / 9;
      const nextWidth = Math.floor(Math.min(maxWidth, widthByHeightLimit));
      const nextHeight = Math.floor((nextWidth * 9) / 16);
      setFullScreenSize({ width: nextWidth, height: nextHeight });
    };
    updateModalSize();
    window.addEventListener('resize', updateModalSize);
    return () => {
      window.removeEventListener('resize', updateModalSize);
    };
  }, [isFullScreen]);

  useEffect(() => {
    apiV2AreaList2Post().then(res => {
      const options = (res?.list || [])
        .filter(item => item.id != null && item.zoneName)
        .map(item => ({
          label: item.zoneName!,
          value: item.id!,
        }));
      setAreaOptions(options);
    });
  }, []);

  useEffect(() => {
    const numericAreaIds = areaIds.map(id => Number(id)).filter(id => !Number.isNaN(id));
    apiV2AreaCameraListPost({ ids: numericAreaIds }).then(res => {
      const options = (res?.list ?? []).map(item => {
        return {
          label: item.deviceNameAlias!,
          value: item.deviceNameAlias!,
        };
      });
      setMonitorOptions(options);
      setMonitorIds(prev => prev.filter(value => options.some(option => option.value === value)));
    });
  }, [areaIds]);

  const currentLayout = useMemo(() => {
    return gridOptions.find(option => option.key === layoutKey) || gridOptions[2];
  }, [layoutKey]);
  const pagingInfo = useSearchParams(currentLayout.pageSize, {});
  const { pageOffset, pageSize, setPageOffset, setTotalCount, setPageSize } = pagingInfo;

  useEffect(() => {
    if (pageSize !== currentLayout.pageSize) {
      setPageOffset(1);
      setPageSize(currentLayout.pageSize);
    }
    // useSearchParams 返回的方法引用不稳定，这里只需要响应 pageSize 数值变化
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLayout.pageSize, pageSize]);

  useEffect(() => {
    const ids = areaIds.map(id => Number(id)).filter(id => !Number.isNaN(id));
    const requestId = latestPageRequestIdRef.current + 1;
    latestPageRequestIdRef.current = requestId;
    setRemoteLoading(true);

    apiV2AreaCameraPagePost({
      ids: ids.length ? ids : undefined,
      deviceNames: monitorIds.length ? monitorIds : undefined,
      page: pageOffset,
      size: currentLayout.pageSize,
    })
      .then(res => {
        if (latestPageRequestIdRef.current !== requestId) {
          return;
        }
        const list = (res?.list || []).map((item, index) => ({
          renderKey: `${pageOffset}-${index}-${item.type ?? 'unknown'}-${item.id ?? item.code ?? 'unknown'}`,
          id: item.id ?? item.code ?? `${pageOffset}-${index}`,
          areaId: '',
          areaName: item.areaName || '',
          monitorDeviceId: item.code || String(item.id || ''),
          monitorName: item.deviceNameAlias || '',
          accessType: item.type,
          accountAccessMode: (item as any).accountAccessMode,
          configType: item.configType,
          platformId: item.id,
          thirdCode: item.code,
          monitorUrl: item.url,
          monitorLocation: item.monitorLocation,
          cameraUsername: item.cameraUsername,
          cameraPassword: item.cameraPassword,
          ipAddress: item.ipAddress,
          port: item.port,
          cameraPort: item.cameraPort,
          rtspPort: item.rtspPort,
          channelIndex: item.channelIndex,
          enableFeature: item.enableFeature,
        }));
        const nextList = list.slice(0, currentLayout.pageSize);
        setRemoteList(nextList);
        setTotalCount(Number(res?.total) || 0);
      })
      .finally(() => {
        if (latestPageRequestIdRef.current === requestId) {
          setRemoteLoading(false);
        }
      });
  }, [areaIds, layoutKey, monitorIds, pageOffset, currentLayout.columns, currentLayout.pageSize, currentLayout.rows]);

  const isFullScreenHK =
    fullScreenItem?.accessType === 1 &&
    (fullScreenItem?.accountAccessMode === AccessMode.HK ||
      (!fullScreenItem?.accountAccessMode && canPlayHK(fullScreenItem)));
  const isFullScreenRtsp =
    (fullScreenItem?.accessType === 1 && fullScreenItem?.accountAccessMode === AccessMode.RTSP) ||
    isRtspUrl(fullScreenUrl);

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? [])]}>
      <div className={styles.toolbar}>
        <div className={styles.filterGroup}>
          <span className={styles.label}>区域名称:</span>
          <Select
            allowClear
            mode="multiple"
            showSearch
            placeholder="请选择"
            optionFilterProp="label"
            className={styles.select}
            value={areaIds}
            options={areaOptions}
            onChange={value => {
              setAreaIds(value);
              setPageOffset(1);
            }}
          />
          <span className={cls(styles.label, styles.secondFieldLabel)}>监控设备名称:</span>
          <Select
            allowClear
            mode="multiple"
            showSearch
            placeholder="请选择"
            optionFilterProp="label"
            className={styles.select}
            value={monitorIds}
            options={monitorOptions}
            onChange={value => {
              setMonitorIds((value as string[]) || []);
              setPageOffset(1);
            }}
          />
        </div>

        <div className={styles.layoutGroup}>
          <span className={styles.label}>布局:</span>
          <div className={styles.segmented}>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={layoutKey}
              onChange={e => {
                const value = e.target.value as string;
                setLayoutKey(value);
                setPageOffset(1);
              }}
              options={[...gridOptions.map(option => ({ label: option.label, value: option.key }))]}
            />
          </div>
        </div>
      </div>
      <Spin spinning={remoteLoading}>
        {remoteList.length === 0 ? (
          <Empty style={{ marginTop: 200 }} />
        ) : (
          <div
            className={styles.grid}
            style={{ gridTemplateColumns: `repeat(${currentLayout.columns}, minmax(0, 1fr))` }}
          >
            {remoteList.map(item => (
              <div key={item.renderKey} className={styles.card}>
                <VideoCardPreview item={item} />
                <div className={styles.cardFooter}>
                  <span className={styles.cardTitle}>{`${item.areaName}-${item.monitorName}`}</span>
                  <Button
                    type="link"
                    className={styles.fullscreenBtn}
                    icon={<ExpandOutlined />}
                    onClick={() => openFullScreen(item)}
                  >
                    全屏
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Spin>
      {!remoteLoading && (
        <div className={styles.paginationBar}>
          <Paging pagingInfo={pagingInfo} showSizeChanger={false} />
        </div>
      )}
      {isFullScreen && (
        <Modal
          open={isFullScreen}
          title={fullScreenName}
          width={fullScreenSize.width}
          onCancel={() => {
            setIsFullScreen(false);
            setFullScreenItem(undefined);
            setFullScreenUrl(undefined);
          }}
          footer={null}
          destroyOnClose
          maskClosable={false}
        >
          {isFullScreenHK ? (
            canPlayHK(fullScreenItem) ? (
              <HKPlayerFrame
                domId="hk_video_view_fullscreen"
                height={fullScreenSize.height}
                monitorLocation={fullScreenItem.monitorLocation}
                cameraUsername={fullScreenItem.cameraUsername}
                cameraPassword={fullScreenItem.cameraPassword}
                ipAddress={fullScreenItem.ipAddress}
                port={fullScreenItem.port}
                cameraPort={fullScreenItem.cameraPort}
                rtspPort={fullScreenItem.rtspPort}
                channelIndex={fullScreenItem.channelIndex}
                enableFeature={fullScreenItem.enableFeature}
              />
            ) : (
              <Empty style={{ margin: '40px 0' }} description="暂无视频" />
            )
          ) : (
            <>
              {isFullScreenRtsp ? (
                <RtspPlayerFrame rtspUrl={fullScreenUrl} width="100%" height={fullScreenSize.height} />
              ) : (
                <HlsVideo src={fullScreenUrl} code={fullScreenCode} height={fullScreenSize.height} minHeight={0} />
              )}
            </>
          )}
        </Modal>
      )}
    </Wrapper>
  );
};

export default VideoMonitorView;
